import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Sparkles, Mail, Check, Trash2, ExternalLink } from "lucide-react";

export default function SuperAdmin() {
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [comps, setComps] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState("");

  const load = async () => {
    const [o, u, c, e] = await Promise.all([
      api.get("/super-admin/overview"),
      api.get("/super-admin/users"),
      api.get("/super-admin/competitions"),
      api.get("/super-admin/enquiries"),
    ]);
    setOverview(o.data); setUsers(u.data); setComps(c.data); setEnquiries(e.data);
  };

  useEffect(() => { load(); }, []);

  const toggleExempt = async (comp) => {
    setBusy(comp.id);
    try {
      await api.patch(`/super-admin/competitions/${comp.id}/billing-exempt`, {
        billing_exempt: !comp.billing_exempt,
      });
      await load();
    } finally {
      setBusy("");
    }
  };

  const toggleHandled = async (enq) => {
    setBusy(enq.id);
    try {
      await api.patch(`/super-admin/enquiries/${enq.id}`, { handled: !enq.handled });
      await load();
    } finally {
      setBusy("");
    }
  };

  const deleteEnquiry = async (enq) => {
    if (!window.confirm(`Delete enquiry from ${enq.name}?`)) return;
    setBusy(enq.id);
    try {
      await api.delete(`/super-admin/enquiries/${enq.id}`);
      await load();
    } finally {
      setBusy("");
    }
  };

  const unhandledCount = enquiries.filter((e) => !e.handled).length;

  return (
    <div className="fade-up space-y-6">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#F5B942] font-semibold">Super Admin</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold mt-1">Platform overview</h1>
      </div>

      {overview && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          {Object.entries({
            "Users": overview.users,
            "Competitions": overview.competitions,
            "Active": overview.active_competitions,
            "Fixtures": overview.fixtures,
            "Predictions": overview.predictions,
            "Subscriptions": overview.subscriptions,
          }).map(([label, v]) => (
            <div key={label} className="p-4 rounded-xl bg-[#0D2038] border border-[#1F3A5C]">
              <div className="text-xs uppercase tracking-wider text-[#AAB7C7]">{label}</div>
              <div className="font-heading text-2xl font-bold mt-1">{v}</div>
            </div>
          ))}
        </div>
      )}

      <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]" data-testid="enquiries-panel">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#C7F500]" />
            <h2 className="font-heading text-lg font-semibold">Enquiries</h2>
            {unhandledCount > 0 && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#F5B942] text-[#071426]" data-testid="enquiries-unhandled">
                {unhandledCount} NEW
              </span>
            )}
          </div>
          <span className="text-xs text-[#AAB7C7]">{enquiries.length} total</span>
        </div>

        {enquiries.length === 0 ? (
          <p className="text-sm text-[#AAB7C7]">No enquiries yet. Anything sent from /contact will appear here.</p>
        ) : (
          <div className="divide-y divide-[#1F3A5C] max-h-[520px] overflow-y-auto -mx-2">
            {enquiries.map((e) => {
              const isOpen = expanded === e.id;
              return (
                <div key={e.id} className={`px-2 py-3 ${e.handled ? "opacity-60" : ""}`} data-testid={`enquiry-${e.id}`}>
                  <button onClick={() => setExpanded(isOpen ? null : e.id)}
                    data-testid={`enquiry-toggle-${e.id}`}
                    className="w-full text-left flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{e.name}</span>
                        {e.organisation && <span className="text-xs px-1.5 py-0.5 rounded bg-[#132A45] text-[#AAB7C7]">{e.organisation}</span>}
                        {e.handled && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#C7F500]/15 text-[#C7F500]">HANDLED</span>}
                      </div>
                      <div className="text-xs text-[#AAB7C7] mt-0.5">
                        <a href={`mailto:${e.email}`} className="hover:text-white" onClick={(ev) => ev.stopPropagation()}>{e.email}</a>
                        {" · "}
                        {new Date(e.created_at).toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </div>
                      {!isOpen && (
                        <p className="text-sm text-[#AAB7C7] mt-1.5 line-clamp-2">{e.message}</p>
                      )}
                    </div>
                    <span className="text-xs text-[#7A8AA3] shrink-0">{isOpen ? "Hide" : "Read"}</span>
                  </button>
                  {isOpen && (
                    <div className="mt-3 space-y-3">
                      <div className="p-3 rounded-lg bg-[#071426] border border-[#1F3A5C] text-sm text-[#E6ECF3] whitespace-pre-wrap">{e.message}</div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <a href={`mailto:${e.email}?subject=Re: your Top of the Lot enquiry`}
                          onClick={(ev) => ev.stopPropagation()}
                          data-testid={`enquiry-reply-${e.id}`}
                          className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-[#C7F500] hover:bg-[#D9FF27] text-[#071426] font-semibold">
                          <ExternalLink className="w-3 h-3" /> Reply via email
                        </a>
                        <button onClick={() => toggleHandled(e)} disabled={busy === e.id}
                          data-testid={`enquiry-handled-${e.id}`}
                          className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-[#132A45] hover:bg-[#1B4276]">
                          <Check className="w-3 h-3" /> {e.handled ? "Mark unhandled" : "Mark handled"}
                        </button>
                        <button onClick={() => deleteEnquiry(e)} disabled={busy === e.id}
                          data-testid={`enquiry-delete-${e.id}`}
                          className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg text-red-400 hover:bg-red-500/10">
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]">
          <h2 className="font-heading text-lg font-semibold mb-3">All competitions</h2>
          <p className="text-xs text-[#AAB7C7] mb-3">Toggle a league to complimentary — no billing UI, unlimited players.</p>
          <div className="divide-y divide-[#1F3A5C]">
            {comps.map((c) => (
              <div key={c.id} className="py-3 flex items-center justify-between gap-3" data-testid={`super-comp-${c.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{c.name}</div>
                  <div className="text-xs text-[#AAB7C7]">{c.season} · {c.member_count} members</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => toggleExempt(c)}
                    disabled={busy === c.id}
                    data-testid={`toggle-exempt-${c.id}`}
                    className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full transition-colors ${
                      c.billing_exempt
                        ? "bg-[#F5B942]/15 text-[#F5B942] hover:bg-[#F5B942]/25"
                        : "bg-[#132A45] text-[#AAB7C7] hover:bg-[#1B4276]"
                    }`}
                  >
                    <Sparkles className="w-3 h-3" />
                    {c.billing_exempt ? "Complimentary" : "Bill normally"}
                  </button>
                  <span className={`text-xs px-2 py-0.5 rounded ${c.status === "active" ? "bg-[#C7F500]/10 text-[#C7F500]" : "bg-[#132A45] text-[#AAB7C7]"}`}>{c.status}</span>
                </div>
              </div>
            ))}
            {comps.length === 0 && <div className="text-sm text-[#AAB7C7]">None yet.</div>}
          </div>
        </div>

        <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]">
          <h2 className="font-heading text-lg font-semibold mb-3">All users</h2>
          <div className="divide-y divide-[#1F3A5C] max-h-96 overflow-y-auto">
            {users.map((u) => (
              <div key={u.id} className="py-3 flex items-center justify-between" data-testid={`super-user-${u.id}`}>
                <div>
                  <div className="font-medium">{u.name}</div>
                  <div className="text-xs text-[#AAB7C7]">{u.email}</div>
                </div>
                <span className="text-xs uppercase text-[#AAB7C7] tracking-wider">{u.role}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
